import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import { useAuth } from '../contexts/AuthContext';
import { BrandingTab } from './Settings/BrandingTab';
import Paper from '@mui/material/Paper';

interface BusinessInfo {
  name: string;
  logo_url?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
}
interface BusinessSettings {
  brand_color?: string;
  default_terms?: string;
  footer_msg?: string;
  late_fee_type?: string;
  late_fee_value?: string | number | null;
  late_fee_days?: string | number | null;
}

// Sedra visual constants (style-only)
const sedra = '#4a274f';
const cardSx = {
  borderRadius: 3,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
  bgcolor: 'background.paper',
} as const;
const btnSx = { borderRadius: 2, textTransform: 'none', fontWeight: 600 } as const;

const SettingsPage: React.FC = () => {
  const { currentBusiness, token, refreshBusinesses, isCookieMode, API_BASE_URL } = useAuth();
  const [tab, setTab] = useState(0);
  const [info, setInfo] = useState<BusinessInfo>({
    name: currentBusiness?.name || '',
    logo_url: currentBusiness?.logo_url || '',
    street: currentBusiness?.street || '',
    city: currentBusiness?.city || '',
    state: currentBusiness?.state || '',
    zip: currentBusiness?.zip || '',
    country: currentBusiness?.country || '',
    phone: currentBusiness?.phone || '',
    email: currentBusiness?.email || '',
    website: currentBusiness?.website || '',
  });
  const [settings, setSettings] = useState<BusinessSettings>({
    brand_color: currentBusiness?.brand_color || '',
    default_terms: currentBusiness?.default_terms || '',
    footer_msg: currentBusiness?.footer_msg || '',
    late_fee_type: currentBusiness?.late_fee_type || '',
    late_fee_value: currentBusiness?.late_fee_value ?? '',
    late_fee_days: currentBusiness?.late_fee_days ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [nickname, setNickname] = useState<string>('');
  const [deleteConfirm, setDeleteConfirm] = useState<string>('');
  const [deleteBusy, setDeleteBusy] = useState<boolean>(false);

  // Load current nickname (always call hooks; guard inside)
  useEffect(() => {
    (async () => {
      if (!currentBusiness) return;
      try {
        const headers: any = {};
        
        // Only add Authorization header if not in cookie mode
        if (!isCookieMode && token) {
          headers.Authorization = `Bearer ${token}`;
        }
        
        const r = await fetch(`${API_BASE_URL}/businesses/${currentBusiness.id}/nickname`, {
          headers,
          credentials: isCookieMode ? 'include' : 'omit',
        });
        if (r.ok) {
          const data = await r.json();
          setNickname(data.nickname || '');
        }
      } catch {}
    })();
  }, [currentBusiness?.id, token]);

  if (!currentBusiness) {
    return <Typography>No business selected.</Typography>;
  }

  const handleSave = async (type: 'info' | 'settings') => {
    setSaving(true);
    setError('');
    setSuccess('');
    if (type === 'info') {
      const body: BusinessInfo = { ...info };
      try {
        const headers: any = { 'Content-Type': 'application/json' };
        if (!isCookieMode && token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(`${API_BASE_URL}/businesses/${currentBusiness.id}`, {
          method: 'PUT',
          headers,
          credentials: isCookieMode ? 'include' : 'omit',
          body: JSON.stringify(body)
        });
        if (!res.ok) {
          const err = await res.json();
          setError(err.error || 'Failed to save');
          setSaving(false);
          return;
        }
        setSuccess('Saved!');
        setSaving(false);
        setError('');
        window.location.reload();
      } catch (e) {
        setError('Failed to save');
        setSaving(false);
      }
    } else {
      const body = { ...settings } as BusinessSettings;
      if (body.late_fee_value === '') body.late_fee_value = null;
      else body.late_fee_value = Number(body.late_fee_value);
      if (body.late_fee_days === '') body.late_fee_days = null;
      else body.late_fee_days = Number(body.late_fee_days);
      try {
        const headers: any = { 'Content-Type': 'application/json' };
        if (!isCookieMode && token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(`${API_BASE_URL}/businesses/${currentBusiness.id}`, {
          method: 'PUT',
          headers,
          credentials: isCookieMode ? 'include' : 'omit',
          body: JSON.stringify(body)
        });
        if (!res.ok) {
          const err = await res.json();
          setError(err.error || 'Failed to save');
          setSaving(false);
          return;
        }
        setSuccess('Saved!');
        setSaving(false);
        setError('');
        window.location.reload();
      } catch (e) {
        setError('Failed to save');
        setSaving(false);
      }
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
      <Typography sx={{ fontSize: 24, fontWeight: 800, letterSpacing: 0.1, mb: 2 }}>
        Business Settings
      </Typography>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{
          mb: 3,
          '& .MuiTabs-indicator': { backgroundColor: sedra },
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 44, borderRadius: 2, mr: 1 },
          '& .Mui-selected': { color: sedra },
        }}
      >
        <Tab label="Business Info" />
        <Tab label="Business Settings" />
        <Tab label="Invoice Settings" />
        <Tab label="Advanced" />
      </Tabs>

      {tab === 0 && (
        <Paper elevation={0} sx={cardSx} className="rounded-2xl">
          <Box sx={{ p: 3 }}>
            <TextField label="Business Name" fullWidth margin="dense" value={info.name} onChange={e => setInfo({ ...info, name: e.target.value })} />
            <TextField label="Logo URL" fullWidth margin="dense" value={info.logo_url} onChange={e => setInfo({ ...info, logo_url: e.target.value })} />
            <TextField label="Street" fullWidth margin="dense" value={info.street} onChange={e => setInfo({ ...info, street: e.target.value })} />
            <TextField label="City" fullWidth margin="dense" value={info.city} onChange={e => setInfo({ ...info, city: e.target.value })} />
            <TextField label="State" fullWidth margin="dense" value={info.state} onChange={e => setInfo({ ...info, state: e.target.value })} />
            <TextField label="ZIP" fullWidth margin="dense" value={info.zip} onChange={e => setInfo({ ...info, zip: e.target.value })} />
            <TextField label="Country" fullWidth margin="dense" value={info.country} onChange={e => setInfo({ ...info, country: e.target.value })} />
            <TextField label="Phone" fullWidth margin="dense" value={info.phone} onChange={e => setInfo({ ...info, phone: e.target.value })} />
            <TextField label="Email" fullWidth margin="dense" value={info.email} onChange={e => setInfo({ ...info, email: e.target.value })} />
            <TextField label="Website" fullWidth margin="dense" value={info.website} onChange={e => setInfo({ ...info, website: e.target.value })} />
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button variant="contained" sx={btnSx} onClick={() => handleSave('info')} disabled={saving}>Save</Button>
            </Box>
          </Box>
        </Paper>
      )}

      {tab === 1 && (
        <Paper elevation={0} sx={{ ...cardSx, mt: 3 }} className="rounded-2xl">
          <Box sx={{ p: 3 }}>
            <TextField label="Default Terms" fullWidth margin="dense" value={settings.default_terms} onChange={e => setSettings({ ...settings, default_terms: e.target.value })} />
            <TextField label="Footer Message" fullWidth margin="dense" value={settings.footer_msg} onChange={e => setSettings({ ...settings, footer_msg: e.target.value })} />
            <TextField label="Late Fee Type" fullWidth margin="dense" value={settings.late_fee_type} onChange={e => setSettings({ ...settings, late_fee_type: e.target.value })} />
            <TextField label="Late Fee Value" type="number" fullWidth margin="dense" value={settings.late_fee_value} onChange={e => setSettings({ ...settings, late_fee_value: e.target.value })} />
            <TextField label="Late Fee Days" type="number" fullWidth margin="dense" value={settings.late_fee_days} onChange={e => setSettings({ ...settings, late_fee_days: e.target.value })} />
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button variant="contained" sx={btnSx} onClick={() => handleSave('settings')} disabled={saving}>Save</Button>
            </Box>
          </Box>
        </Paper>
      )}

      {tab === 2 && <BrandingTab />}

      {tab === 3 && (
        <Paper elevation={0} sx={{ ...cardSx, mt: 3 }} className="rounded-2xl">
          <Box sx={{ p: 3, display: 'grid', gap: 2 }}>
            <Typography variant="h6">Private nickname</Typography>
            <TextField
              label="Nickname (only visible to you)"
              value={nickname}
              onChange={e => setNickname(e.target.value)}
              helperText="Shown only in your UI next to business name. Not used anywhere else."
            />
            <Button variant="outlined" sx={btnSx} onClick={async () => {
              try {
                const headers: any = { 'Content-Type': 'application/json' };
                if (!isCookieMode && token) headers.Authorization = `Bearer ${token}`;
                await fetch(`${API_BASE_URL}/businesses/${currentBusiness.id}/nickname`, {
                  method: 'PUT',
                  headers,
                  credentials: isCookieMode ? 'include' : 'omit',
                  body: JSON.stringify({ nickname: nickname || null })
                });
                await refreshBusinesses();
                setSuccess('Nickname saved');
                setTimeout(() => setSuccess(''), 2000);
              } catch (e) {
                setError('Failed to save nickname');
              }
            }}>Save Nickname</Button>

            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" color="error">Delete business</Typography>
            <Typography variant="body2">Type the business name exactly to confirm: <strong>{currentBusiness.name}</strong></Typography>
            <TextField
              label="Confirm name"
              value={deleteConfirm}
              onChange={e => setDeleteConfirm(e.target.value)}
            />
            <Button color="error" variant="contained" sx={btnSx} disabled={deleteConfirm !== currentBusiness.name || deleteBusy}
              onClick={async () => {
                setDeleteBusy(true);
                setError(''); setSuccess('');
                try {
                  const headers: any = { 'Content-Type': 'application/json' };
                  if (!isCookieMode && token) headers.Authorization = `Bearer ${token}`;
                  const r = await fetch(`${API_BASE_URL}/businesses/${currentBusiness.id}`, {
                    method: 'DELETE',
                    headers,
                    credentials: isCookieMode ? 'include' : 'omit',
                    body: JSON.stringify({ confirm_name: deleteConfirm })
                  });
                  if (!r.ok) {
                    const e = await r.json().catch(() => ({}));
                    throw new Error(e.error || 'Failed to delete');
                  }
                  window.location.reload();
                } catch (e: any) {
                  setError(e.message || 'Failed to delete');
                } finally {
                  setDeleteBusy(false);
                }
              }}
            >Delete Business</Button>
          </Box>
        </Paper>
      )}

      {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
      {success && <Typography color="success.main" sx={{ mt: 2 }}>{success}</Typography>}
    </div>
  );
};

export default SettingsPage; 